import { apiDelete, apiGet, apiPatch, apiPost, isNotFoundError } from '../../core/index.js'
import type { CreateWorkerLoginInput, Invitation, InvitationResult, InviteWorkerInput, ResetLink, UpdateWorkerLoginInput, WorkerInvitationState, WorkerLogin } from '../index.js'

/**
 * The calls behind a worker's own sign-in, under
 * `/admin/clients/{clientId}/facilities/{facilityId}/workers/{workerId}/login`,
 * as an adapter so the same Login section sits in the operator's Edit worker
 * dialog (bearer token) and the admin's worker detail (Basic Auth).
 */
export interface WorkerLoginApi {
  /** The login, or null when the worker has none (the API's 404). */
  get(): Promise<WorkerLogin | null>
  create(input: CreateWorkerLoginInput): Promise<WorkerLogin & { password: string }>
  update(input: UpdateWorkerLoginInput): Promise<WorkerLogin & { password?: string }>
  remove(): Promise<void>
  resetLink(): Promise<ResetLink>
  /** The worker's open invitation and what a text could go to (invitations plan phase 3). */
  invitation(): Promise<WorkerInvitationState>
  /** Invites the worker to the app: the login is made pending, the email (and a text, where asked) goes out. */
  invite(input: InviteWorkerInput): Promise<InvitationResult>
  resendInvitation(): Promise<InvitationResult>
  /** Withdraws the invitation and the pending login with it. */
  revokeInvitation(): Promise<Invitation>
}

export function workerLoginPath(clientId: string, facilityId: string, workerId: string): string {
  return `/admin/clients/${encodeURIComponent(clientId)}/facilities/${encodeURIComponent(facilityId)}/workers/${encodeURIComponent(workerId)}/login`
}

/** The operator app's adapter: its own client's path, with the session's bearer token. */
export function operatorWorkerLoginApi(clientId: string, facilityId: string, workerId: string): WorkerLoginApi {
  const path = workerLoginPath(clientId, facilityId, workerId)
  return {
    get: async () => {
      try {
        return await apiGet<WorkerLogin>(path)
      } catch (error) {
        if (isNotFoundError(error)) {
          return null
        }
        throw error
      }
    },
    create: (input) => apiPost(path, input),
    update: (input) => apiPatch(path, input),
    remove: () => apiDelete(path),
    resetLink: () => apiPost(`${path}/reset-link`, undefined),
    invitation: () => apiGet(`${path}/invitation`),
    invite: (input) => apiPost(`${path}/invitation`, input),
    resendInvitation: () => apiPost(`${path}/invitation/resend`, {}),
    revokeInvitation: () => apiPost(`${path}/invitation/revoke`, {}),
  }
}

/** The query key the Login section reads under; the roster and worker edits leave it alone. */
export function workerLoginQueryKey(clientId: string, workerId: string): readonly string[] {
  return ['worker-login', clientId, workerId]
}
